import { faBullhorn } from "@fortawesome/free-solid-svg-icons/faBullhorn";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Spinner } from "../../../components/Spinner";
import { Text } from "../../../components/Text";
import { CampaignDrip } from "../../../model/campaign/CampaignDrip";
import { campaignActions } from "../../../redux/campaign/campaign.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Switch, Tooltip } from "../../../utils/local-antd";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { TextWithTruncated } from "../../../components/TextWithTruncated";

const Container = styled(Flex)`
  gap: ${Spacing.m};
  padding: 0 ${Spacing.m};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${Shadow.SM};
  align-items: center;
  position: relative;
  width: 100%;
`;

const StatusButton = styled(Button)<{ enabled: boolean }>`
  padding: 0;
  margin: 0;
  background-color: transparent;
  color: ${({ theme, enabled }) => theme[enabled ? "success" : "danger"]};
  position: absolute;
  top: calc(${Spacing.s} / 2);
  html[dir="rtl"] & {
    right: calc(${Spacing.s} / 2);
  }
  html[dir="ltr"] & {
    left: calc(${Spacing.s} / 2);
  }
`;

const EditButton = styled(Button)`
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  margin-inline-start: auto;
  padding: ${Spacing.xs};
`;

const StyledSpinner = styled(Spinner)<{ loading: boolean }>`
  ${({ loading }) =>
    !loading
      ? `
    visibility: hidden;
    pointer-events: none;
  `
      : ""}
`;

type DripCampaignProps = {
  campaign: CampaignDrip;
  onEdit: VoidFunction;
};

export const DripCampaign: React.FC<DripCampaignProps> = ({
  campaign,
  onEdit,
}) => {
  const [toggleActivateLoading, setToggleActivateLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const onToggleActivate = async (active: boolean) => {
    try {
      setToggleActivateLoading(true);

      await dispatch(
        campaignActions.updateCampaign({
          campaignId: campaign._id,
          userId,
          active,
        }),
      ).unwrap();

      await dispatch(campaignActions.getCampaigns({ userId })).unwrap();

      AtiraToast.success(
        t(active ? "campaign.activate.success" : "campaign.deactivate.success"),
      );
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setToggleActivateLoading(false);
    }
  };

  return (
    <Container>
      <AtiraIcon icon={faBullhorn} size="3x" color={"main"} />
      <Flex flex={1} alignItems="center">
        <Flex gap="s" alignItems="center">
          <Tooltip title={campaign.name}>
            <TextWithTruncated length={Lengths.NAME} fontSize="l">
              {campaign.name}
            </TextWithTruncated>
          </Tooltip>
          <Text fontSize="l">|</Text>

          <Text fontSize="l" color="darkerSub">
            {campaign.form?.title}
          </Text>
        </Flex>

        <Tooltip title={t("common.edit")}>
          <EditButton onClick={onEdit} icon={faEdit} iconWidth="2xl" />
        </Tooltip>

        <Flex alignItems="center" gap="s">
          <StyledSpinner
            size="1rem"
            color="main"
            loading={toggleActivateLoading}
          />

          <Tooltip
            title={t(
              campaign.active
                ? "campaign.actions.deactivate"
                : "campaign.actions.activate",
            )}
          >
            <Switch
              checked={campaign.active}
              checkedChildren={t("campaign.status.active")}
              unCheckedChildren={t("campaign.status.inactive")}
              onChange={onToggleActivate}
              disabled={toggleActivateLoading}
            />
          </Tooltip>
        </Flex>
      </Flex>

      <Tooltip title={t(campaign.active ? "common.active" : "common.inactive")}>
        <StatusButton
          enabled={Boolean(campaign.active)}
          icon={faCircle}
          iconWidth="xs"
        />
      </Tooltip>
    </Container>
  );
};
